import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import type { TooltipPlacementType } from '@unifyapps/defs/types/page/walkthrough';
import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { TOOLTIP_BASE_STYLE, TOOLTIP_OFFSET } from '../constants';

interface AnimationState {
  isAnimating: boolean;
  animationProgress: number;
}

/**
 * Hook that handles tooltip positioning and visibility for walkthroughs.
 * Uses Floating UI for positioning
 */
export function useWalkthroughTooltip(
  animationState: AnimationState,
  tooltipPlacementBehavior?: 'auto' | 'manual',
  tooltipPlacement?: TooltipPlacementType,
) {
  const [shouldRenderStepBlocks, setShouldRenderStepBlocks] = useState(true);

  const isAutomatic = tooltipPlacementBehavior === 'auto';
  const currentPlacement = isAutomatic ? undefined : tooltipPlacement;

  // Set up Floating UI to position tooltip relative to target element
  // Use autoUpdate to automatically reposition on scroll/resize
  const { x, y, refs, strategy, placement } = useFloating({
    middleware: [offset(TOOLTIP_OFFSET), flip(), shift()],
    placement: currentPlacement,
    whileElementsMounted: autoUpdate,
  });

  // Calculate tooltip opacity based on animation state
  const tooltipOpacity = useMemo(() => {
    if (!animationState.isAnimating) return 1;
    if (animationState.animationProgress < 0.5) return 0;
    return (animationState.animationProgress - 0.5) * (1 / 0.5);
  }, [animationState.isAnimating, animationState.animationProgress]);

  // Handle tooltip visibility based on animation progress
  useEffect(() => {
    if (animationState.animationProgress >= 0.5 && !shouldRenderStepBlocks) {
      setShouldRenderStepBlocks(true);
    } else if (animationState.animationProgress <= 0.1 && shouldRenderStepBlocks) {
      setShouldRenderStepBlocks(false);
    }
  }, [animationState.animationProgress, shouldRenderStepBlocks]);

  // Calculate tooltip style
  const tooltipStyle = useMemo<CSSProperties>(() => {
    // If no target element, use fixed positioning centered on container
    const finalX = x ?? 0;
    const finalY = y ?? 0;

    return {
      ...TOOLTIP_BASE_STYLE,
      position: strategy,
      top: finalY,
      left: finalX,
      opacity: tooltipOpacity,
      pointerEvents: tooltipOpacity > 0 ? 'auto' : 'none',
    };
  }, [strategy, tooltipOpacity, x, y]);

  return {
    tooltipStyle,
    placement,
    strategy,
    x,
    y,
    refs,
    shouldRenderStepBlocks,
  };
}
